import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, TestTube, Unlink, Plus, ArrowUpRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { messagingChannelMeta } from './channelMeta';
import { StatusPill } from './StatusPill';
import { CountBadge } from './CountBadge';
import { itemVariants } from './motion';
import type { PlatformAccount } from '@/types/api';

interface PlatformConnectionCardProps {
  platform: 'facebook' | 'instagram' | 'whatsapp';
  isConnected: boolean;
  accounts: PlatformAccount[];
  onConnect: () => void;
  onTest: (accountId: string) => void;
  onDisconnect: (accountId: string) => void;
  isConnecting?: boolean;
  isTesting?: boolean;
  isDisconnecting?: boolean;
}

/**
 * Messaging-channel card with uniform collapsed height and expandable account management.
 */
export const PlatformConnectionCard: React.FC<PlatformConnectionCardProps> = ({
  platform,
  isConnected,
  accounts,
  onConnect,
  onTest,
  onDisconnect,
  isConnecting = false,
  isTesting = false,
  isDisconnecting = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const meta = messagingChannelMeta[platform];
  const Icon = meta.icon;
  const needsAttention = isConnected && accounts.some((account) => !account.isActive);

  const accountNames = accounts
    .map((a) => a.accountName || (a as any).pageName || (a as any).phoneNumberId || 'Account')
    .join(', ');

  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card className="flex h-full max-w-none flex-col overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm">
        {/* Pod Header */}
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-lg shadow-2xs', meta.classes.iconBg)}>
              <Icon className={cn('h-3.5 w-3.5', meta.classes.iconText)} />
            </div>
            <h3 className="truncate text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              {meta.name}
            </h3>
            <StatusPill
              connected={isConnected}
              needsAttention={needsAttention}
              tooltipText={
                needsAttention
                  ? `${meta.name}: Re-authorization required`
                  : isConnected
                    ? `${meta.name}: Connected & Active (${accounts.length} account${accounts.length > 1 ? 's' : ''})`
                    : `${meta.name}: Not connected`
              }
            />
            {isConnected && accounts.length > 0 && (
              <CountBadge
                count={accounts.length}
                tooltipText={`${accounts.length} active ${meta.name} account${accounts.length > 1 ? 's' : ''}: ${accountNames}`}
                variant={needsAttention ? 'amber' : 'neutral'}
              />
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {isConnected ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-7 px-2 rounded-lg text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors gap-1"
                  title={isExpanded ? 'Collapse accounts' : 'Manage accounts'}
                >
                  <span>{isExpanded ? 'Hide' : 'Manage'}</span>
                  <ChevronDown className={cn('h-3 w-3 transition-transform duration-200', isExpanded && 'rotate-180')} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onConnect}
                  disabled={isConnecting}
                  className="h-7 px-2 rounded-lg border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-800 text-[11px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors gap-1 shadow-2xs"
                  title="Add another account"
                >
                  {isConnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  <span>Add</span>
                </Button>
              </>
            ) : (
              <Button
                onClick={onConnect}
                disabled={isConnecting}
                size="sm"
                className={cn('h-7 px-3 rounded-lg text-xs font-semibold shadow-2xs transition-colors gap-1.5', meta.classes.connectBtn)}
              >
                {isConnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowUpRight className="h-3 w-3" />}
                <span>Connect</span>
              </Button>
            )}
          </div>
        </div>

        {/* Pod Body */}
        <div className="flex flex-1 flex-col justify-between p-3 bg-white/70 dark:bg-slate-900/70">
          <div className="space-y-2">
            <p className="line-clamp-1 text-xs text-slate-500 dark:text-slate-400">{meta.description}</p>

            {/* Collapsed summary line */}
            {isConnected ? (
              !isExpanded && (
                <div className="flex items-center justify-between rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-850/60 px-2.5 py-1.5 text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', needsAttention ? 'bg-amber-500' : 'bg-emerald-500')} />
                    <span className="truncate font-semibold text-slate-800 dark:text-slate-200">
                      {accounts[0]?.accountName || (accounts[0] as any)?.pageName || 'Connected account'}
                    </span>
                    {accounts.length > 1 && (
                      <span className="text-slate-400 dark:text-slate-500 font-normal">+{accounts.length - 1} more</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="text-[11px] font-semibold text-primary hover:underline ml-2 shrink-0"
                  >
                    Details
                  </button>
                </div>
              )
            ) : (
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200/60 dark:border-slate-800/80 bg-slate-50/40 dark:bg-slate-850/30 px-2.5 py-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" />
                <span>No active connections</span>
              </div>
            )}

            {/* Expanded account management tray */}
            <AnimatePresence initial={false}>
              {isConnected && isExpanded && (
                <motion.div
                  key="account-list"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-1.5 overflow-hidden pt-1"
                >
                  {accounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center justify-between gap-2 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-2.5 py-1.5 shadow-2xs"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-slate-800 dark:text-slate-200">
                          {account.accountName || (account as any)?.pageName || (account as any)?.phoneNumberId || (account as any)?.pageId || 'Connected account'}
                        </p>
                        {!account.isActive && (
                          <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400">Needs re-authorization</p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg gap-1"
                          onClick={() => onTest(account.id)}
                          disabled={isTesting}
                          title="Test connection"
                          aria-label={`Test ${account.accountName}`}
                        >
                          {isTesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube className="h-3 w-3 text-slate-500" />}
                          <span>Test</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg gap-1"
                          onClick={() => onDisconnect(account.id)}
                          disabled={isDisconnecting}
                          title="Disconnect"
                          aria-label={`Disconnect ${account.accountName}`}
                        >
                          {isDisconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                          <span>Disconnect</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default PlatformConnectionCard;
